import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, Shield, Wrench, ThumbsUp, Phone, Building2 } from 'lucide-react';
import { QuoteForm } from '../components/QuoteForm';
import { FAQAccordion } from '../components/FAQAccordion';
const commercialData: Record<string, any> = {
  silicone: {
    title: 'Silicone Roof Coatings',
    image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&q=80',
    description: 'Silicone roof coatings offer a cost-effective way to extend the life of your existing commercial flat roof by up to 20 years without a full tear-off. Applied as a seamless, monolithic membrane, silicone coatings eliminate seams and fastener penetrations — the most common sources of leaks on commercial roofs.',
    benefits: [{
      title: 'No Tear-Off Required',
      desc: 'Apply directly over your existing roof substrate, saving time and disposal costs.',
      icon: Shield
    }, {
      title: 'Seamless Protection',
      desc: 'Creates a continuous waterproof membrane with zero seams or joints.',
      icon: Wrench
    }, {
      title: 'Energy Savings',
      desc: 'Highly reflective white surface reduces cooling costs by up to 30%.',
      icon: ThumbsUp
    }],
    process: ['Roof Inspection & Core Sampling', 'Surface Cleaning & Preparation', 'Seam & Penetration Reinforcement', 'Silicone Coating Application (2 coats)', 'Final Inspection & Warranty Documentation'],
    faqs: [{
      question: 'How long does a silicone roof coating last?',
      answer: 'A properly applied silicone coating system typically lasts 15 to 20 years. It can also be recoated at the end of its life cycle for continued protection.'
    }, {
      question: 'Can silicone be applied over any existing roof?',
      answer: 'Silicone coatings work well over most substrates including TPO, EPDM, metal, modified bitumen, and built-up roofing. A core sample helps us determine compatibility.'
    }, {
      question: 'Is silicone coating cheaper than a full roof replacement?',
      answer: 'Yes, typically 50-70% less expensive than a full tear-off and replacement, while providing comparable protection and longevity.'
    }]
  },
  tpo: {
    title: 'TPO Roofing Systems',
    image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&q=80',
    description: "Thermoplastic Polyolefin (TPO) is one of the fastest-growing commercial roofing systems in the industry. Its heat-welded seams create a watertight bond stronger than the membrane itself, making it ideal for Michigan's freeze-thaw cycles and severe weather.",
    benefits: [{
      title: 'Heat-Welded Seams',
      desc: 'Hot-air welded seams are 3-4x stronger than adhesive-based systems.',
      icon: Shield
    }, {
      title: 'Energy Star Rated',
      desc: 'White reflective surface meets Energy Star requirements and reduces cooling costs.',
      icon: Wrench
    }, {
      title: 'Chemical Resistant',
      desc: 'Resists grease, oils, and chemical exposure common in commercial environments.',
      icon: ThumbsUp
    }],
    process: ['Structural Assessment & Specification', 'Insulation Board Installation', 'TPO Membrane Roll-Out & Positioning', 'Hot-Air Seam Welding', 'Flashing, Edge Metal & Penetration Detailing'],
    faqs: [{
      question: 'How long does a TPO roof last?',
      answer: 'A quality TPO system installed by certified professionals typically lasts 20 to 30 years with proper maintenance.'
    }, {
      question: 'Is TPO better than EPDM?',
      answer: "Both are excellent systems. TPO offers superior heat-welded seams and reflectivity, while EPDM is known for its flexibility and proven long-term track record. We help you choose based on your building's specific needs."
    }, {
      question: 'Can TPO be installed in cold weather?',
      answer: 'Yes, TPO can be installed year-round. The hot-air welding process works effectively even in colder temperatures, making it well-suited for Michigan.'
    }]
  },
  epdm: {
    title: 'EPDM Roofing Systems',
    image: 'https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=1920&q=80',
    description: "EPDM (Ethylene Propylene Diene Monomer) is a synthetic rubber membrane with a proven track record spanning over 60 years. Known for its exceptional durability and flexibility, EPDM performs reliably in Michigan's extreme temperature swings from -40°F winters to 90°F summers.",
    benefits: [{
      title: 'Extreme Flexibility',
      desc: 'Rubber membrane stretches and contracts with temperature changes without cracking.',
      icon: Shield
    }, {
      title: 'Proven Longevity',
      desc: 'EPDM roofs routinely last 30+ years with minimal maintenance requirements.',
      icon: Wrench
    }, {
      title: 'Cost Effective',
      desc: 'Lower material and installation costs compared to other single-ply systems.',
      icon: ThumbsUp
    }],
    process: ['Roof Deck Inspection & Preparation', 'Insulation & Cover Board Installation', 'EPDM Membrane Placement', 'Seam Adhesion & Detailing', 'Edge Termination & Penetration Sealing'],
    faqs: [{
      question: 'How long does an EPDM roof last?',
      answer: 'EPDM roofs commonly last 25 to 40 years. Many original EPDM installations from the 1970s are still performing today.'
    }, {
      question: 'Is EPDM good for Michigan weather?',
      answer: "Excellent. EPDM's rubber composition handles freeze-thaw cycles, hail, and UV exposure better than many other membrane systems."
    }, {
      question: 'Can EPDM be repaired if damaged?',
      answer: 'Yes, EPDM is one of the easiest commercial roofing systems to repair. Patches can be applied quickly and effectively to restore waterproof integrity.'
    }]
  },
  repair: {
    title: 'Commercial Roof Repair',
    image: 'https://images.unsplash.com/photo-1632759145351-1d592919f522?w=1920&q=80',
    description: 'A leaking commercial roof can disrupt operations, damage inventory, and create safety hazards. Moceri Residential Services provides fast, reliable commercial roof repairs for all flat roof types — from emergency leak fixes to comprehensive restoration projects.',
    benefits: [{
      title: 'Rapid Response',
      desc: 'We prioritize commercial repair calls to minimize business disruption and interior damage.',
      icon: Shield
    }, {
      title: 'All Roof Types',
      desc: 'We repair TPO, EPDM, silicone, modified bitumen, built-up, and metal roof systems.',
      icon: Wrench
    }, {
      title: 'Honest Assessment',
      desc: "We'll tell you if a repair will solve the problem or if replacement is the smarter investment.",
      icon: ThumbsUp
    }],
    process: ['Emergency Tarping (if needed)', 'Comprehensive Leak Investigation', 'Repair Scope & Cost Estimate', 'Professional Repair Execution', 'Post-Repair Inspection & Documentation'],
    faqs: [{
      question: 'How quickly can you respond to a commercial roof leak?',
      answer: 'We prioritize emergency commercial calls and can typically have a crew on-site within 24 hours for tarping and assessment.'
    }, {
      question: 'Do you work with commercial insurance claims?',
      answer: 'Yes, we have extensive experience documenting commercial roof damage and working with insurance adjusters to ensure proper claim coverage.'
    }, {
      question: 'When should I repair vs. replace my commercial roof?',
      answer: "If damage is localized and the overall membrane is in good condition, repair is usually the best option. If you're experiencing recurring leaks in multiple areas, a coating or replacement may be more cost-effective long-term."
    }]
  }
};
export function CommercialServicePage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  const data = commercialData[slug || ''] || commercialData['repair'];
  return <div className="w-full bg-white" data-id="element-634">
      {/* Hero */}
      <section className="relative min-h-[40vh] md:min-h-[50vh] flex items-center pt-20 pb-16 overflow-hidden" data-id="element-635">
        <div className="absolute inset-0 z-0" data-id="element-636">
          <img src={data.image} alt="" className="absolute inset-0 w-full h-full object-cover" data-id="element-637" />
          <div className="absolute inset-0 bg-brand-blue/15 z-[1]" data-id="element-638"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/30 z-[2]" data-id="element-639"></div>
        </div>
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full" data-id="element-640">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="max-w-3xl" data-id="element-641">
            <nav className="flex items-center text-sm text-gray-300 font-body mb-6" data-id="element-642">
              <Link to="/" className="hover:text-brand-blue transition-colors" data-id="element-643">
                Home
              </Link>
              <ChevronRight size={16} className="mx-2" data-id="element-644" />
              <Link to="/commercial" className="hover:text-brand-blue transition-colors" data-id="element-645">
                Commercial
              </Link>
              <ChevronRight size={16} className="mx-2" data-id="element-646" />
              <span className="text-white font-semibold" data-id="element-647">{data.title}</span>
            </nav>
            <div className="inline-flex items-center gap-2 bg-brand-blue/20 border border-brand-blue/50 text-white px-4 py-1.5 rounded-full font-body text-sm font-bold mb-6 backdrop-blur-sm" data-id="element-648">
              <Building2 size={16} data-id="element-649" /> Commercial Division
            </div>
            <h1 className="font-heading font-black text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-6" data-id="element-650">
              {data.title}{' '}
              <span className="block text-brand-blue text-3xl md:text-4xl mt-2" data-id="element-651">
                for Southeast Michigan Businesses
              </span>
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Intro + Form */}
      <section className="py-16 bg-gray-50 border-b border-gray-200" data-id="element-652">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-653">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-20" data-id="element-654">
            <div className="w-full lg:w-7/12" data-id="element-655">
              <h2 className="font-heading font-bold text-3xl text-brand-gray-dark mb-6" data-id="element-656">
                {data.title} Experts
              </h2>
              <div className="w-16 h-1 bg-brand-blue mb-8 rounded-full" data-id="element-657"></div>
              <p className="font-body text-lg text-brand-gray leading-relaxed mb-10" data-id="element-658">
                {data.description}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-659">
                {data.benefits.map((benefit: any, index: number) => <div key={index} className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm text-center" data-id="element-660">
                    <div className="w-14 h-14 bg-brand-blue/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-661">
                      <benefit.icon size={28} className="text-brand-blue" data-id="element-662" />
                    </div>
                    <h3 className="font-heading font-bold text-lg text-brand-gray-dark mb-2" data-id="element-663">
                      {benefit.title}
                    </h3>
                    <p className="font-body text-sm text-brand-gray" data-id="element-664">
                      {benefit.desc}
                    </p>
                  </div>)}
              </div>
            </div>
            <div className="w-full lg:w-5/12" data-id="element-665">
              <div className="-mt-24 relative z-30" data-id="element-666">
                <QuoteForm data-id="element-667" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-brand-gray-dark text-white" data-id="element-668">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-669">
          <div className="flex flex-col md:flex-row gap-12 items-center" data-id="element-670">
            <div className="w-full md:w-1/2" data-id="element-671">
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-4" data-id="element-672">
                Installation Process
              </h2>
              <div className="w-20 h-1 bg-brand-blue mb-8 rounded-full" data-id="element-673"></div>
              <p className="font-body text-gray-300 text-lg" data-id="element-674">
                Our systematic approach ensures quality results and minimal
                disruption to your business operations.
              </p>
            </div>
            <div className="w-full md:w-1/2 space-y-4" data-id="element-675">
              {data.process.map((step: string, index: number) => <div key={index} className="flex items-center gap-4 bg-white/5 p-4 rounded-lg border border-white/10" data-id="element-676">
                  <div className="w-10 h-10 rounded-full bg-brand-blue flex items-center justify-center font-heading font-bold text-lg shrink-0" data-id="element-677">
                    {index + 1}
                  </div>
                  <span className="font-body font-semibold text-lg" data-id="element-678">
                    {step}
                  </span>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-gray-50" data-id="element-679">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-680">
          <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-681">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-brand-gray-dark mb-4" data-id="element-682">
              Frequently Asked Questions
            </h2>
            <div className="w-20 h-1 bg-brand-blue mx-auto mb-6 rounded-full" data-id="element-683"></div>
          </div>
          <FAQAccordion items={data.faqs} data-id="element-684" />
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-brand-blue text-center" data-id="element-685">
        <div className="max-w-4xl mx-auto px-4" data-id="element-686">
          <h2 className="font-heading font-bold text-3xl text-white mb-6" data-id="element-687">
            Get a Commercial Roofing Quote
          </h2>
          <p className="font-body text-xl text-blue-100 mb-8" data-id="element-688">
            Contact us today for a free assessment of your commercial roof.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-689">
            <a href="#quote" className="bg-white text-brand-blue font-body font-bold text-lg py-4 px-10 rounded-lg hover:bg-gray-50 transition-colors shadow-lg" data-id="element-690">
              Request a Quote
            </a>
            <a href="tel:5862033701" className="bg-transparent border-2 border-white text-white font-body font-bold text-lg py-4 px-10 rounded-lg hover:bg-white/10 transition-colors flex items-center justify-center gap-2" data-id="element-691">
              <Phone size={20} data-id="element-692" /> (586) 203-3701
            </a>
          </div>
        </div>
      </section>
    </div>;
}